import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { auditKeys, complianceUserKeys } from "@/lib/query";

export interface AuditTemplateRef {
  id: string;
  name: string;
}

export interface AuditListItem {
  id: string;
  templates: AuditTemplateRef[];
  projectId: string | null;
  projectName: string;
  accountName: string | null;
  assignedToId: string;
  assignedToName: string | null;
  createdById: string;
  createdByName: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  totalItems: number;
  checkedItems: number;
  /** An unfinished audit with no activity for the configured number of days. */
  isStale: boolean;
  idleDays: number;
}

export interface AuditItemNoteRow {
  id: string;
  auditItemId: string;
  authorId: string;
  authorName: string | null;
  body: string;
  createdAt: string;
}

export interface AuditItemRow {
  id: string;
  auditId: string;
  templateItemId: string | null;
  sectionTitle: string;
  sectionOrder: number;
  itemText: string;
  itemOptional: boolean;
  itemUpload: boolean;
  itemHelpText: string | null;
  itemOrder: number;
  checked: boolean;
  latestNote: AuditItemNoteRow | null;
  noteCount: number;
  updatedAt: string;
}

export interface AuditSection {
  sectionTitle: string;
  sectionOrder: number;
  items: AuditItemRow[];
}

export interface AuditNoteRow {
  id: string;
  auditId: string;
  authorId: string;
  authorName: string | null;
  body: string;
  context: "general" | "blocked" | "resolution";
  createdAt: string;
}

export interface AuditDetail extends AuditListItem {
  sections: AuditSection[];
  items: AuditItemRow[];
  notes: AuditNoteRow[];
  findings: string | null;
  blockedBy: "project_team" | "compliance_team" | null;
  assignedTo: { id: string; name: string; email: string } | null;
  createdBy: { id: string; name: string; email: string } | null;
  completedBy: { id: string; name: string } | null;
  guidanceRequestedAt: string | null;
  kickoffEmailSentAt: string | null;
  kickoffEmailSentBy: { id: string; name: string } | null;
  kickoffEmailTo: string[] | null;
  kickoffEmailCc: string[] | null;
  kickoffEmailMissing: string[] | null;
  kickoffEmailSubject: string | null;
  kickoffEmailBody: string | null;
}

export const BLOCKED_BY_LABELS: Record<"project_team" | "compliance_team", string> = {
  project_team: "Waiting on Project Team",
  compliance_team: "Needs Compliance Team Review",
};

export interface ComplianceUser {
  id: string;
  name: string;
  email: string;
  role: string;
  roles: string[];
}

export function useAudits(filters?: { status?: string }) {
  return useQuery({
    queryKey: auditKeys.list(filters),
    queryFn: async (): Promise<AuditListItem[]> => {
      const params = new URLSearchParams();
      if (filters?.status) params.set("status", filters.status);
      const res = await fetch(`/api/audits?${params}`);
      if (!res.ok) throw new Error("Failed to fetch audits");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useAudit(id: string | undefined) {
  return useQuery({
    queryKey: id ? auditKeys.detail(id) : (["audits", "detail", null] as const),
    enabled: !!id,
    queryFn: async (): Promise<AuditDetail> => {
      const res = await fetch(`/api/audits/${id}`);
      if (!res.ok) throw new Error("Failed to fetch audit");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreateAudit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      templateIds: string[];
      projectId?: string;
      projectName: string;
      assignedToId: string;
    }) => {
      const res = await fetch("/api/audits", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create audit");
      const json = await res.json();
      return json.data as AuditListItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.all });
      toast.success("Audit created");
    },
    onError: () => {
      toast.error("Failed to create audit");
    },
  });
}

export function useRequestGuidance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      templateIds: string[];
      projectId?: string;
      projectName: string;
      assignedToId: string;
      message: string;
      guidanceHours: number;
    }) => {
      const res = await fetch("/api/audits/guidance-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to send guidance request");
      return json.data as AuditListItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.all });
      toast.success("Guidance request sent");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to send guidance request");
    },
  });
}

export function useRequestGuidanceForAudit(auditId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { message: string; guidanceHours: number }) => {
      const res = await fetch(`/api/audits/${auditId}/guidance-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to send guidance request");
      return json.data as AuditListItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.all });
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
      toast.success("Guidance request sent");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to send guidance request");
    },
  });
}

export interface GuidanceRequestLogEntry {
  id: string;
  hours: number | null;
  message: string;
  statusAtRequest: string;
  slackDelivered: boolean;
  createdAt: string;
  requestedByName: string | null;
}

export function useGuidanceRequests(auditId: string, enabled: boolean) {
  return useQuery({
    queryKey: auditKeys.guidanceRequests(auditId),
    enabled,
    queryFn: async () => {
      const res = await fetch(`/api/audits/${auditId}/guidance-requests`);
      if (!res.ok) throw new Error("Failed to load guidance requests");
      const json = await res.json();
      return json.data as GuidanceRequestLogEntry[];
    },
  });
}

export function useUpdateAuditItems(auditId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (items: Array<{ id: string; checked: boolean }>) => {
      const res = await fetch(`/api/audits/${auditId}/items`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error("Failed to update audit items");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
    },
    onError: () => {
      toast.error("Failed to save changes");
    },
  });
}

export function useAuditItemNotes(auditId: string, itemId: string | null) {
  return useQuery({
    queryKey: itemId ? auditKeys.itemNotes(auditId, itemId) : (["audit-item-notes", null] as const),
    enabled: !!itemId,
    queryFn: async (): Promise<AuditItemNoteRow[]> => {
      const res = await fetch(`/api/audits/${auditId}/items/${itemId}/notes`);
      if (!res.ok) throw new Error("Failed to fetch item notes");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreateAuditItemNote(auditId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) => {
      const res = await fetch(`/api/audits/${auditId}/items/${itemId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      });
      if (!res.ok) throw new Error("Failed to add note");
      const json = await res.json();
      return json.data as AuditItemNoteRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.itemNotes(auditId, itemId) });
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
    },
    onError: () => {
      toast.error("Failed to add note");
    },
  });
}

export function useUpdateAuditItemNote(auditId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ noteId, body }: { noteId: string; body: string }) => {
      const res = await fetch(`/api/audits/${auditId}/items/${itemId}/notes/${noteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to update note");
      return json.data as AuditItemNoteRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.itemNotes(auditId, itemId) });
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update note");
    },
  });
}

export function useDeleteAuditItemNote(auditId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (noteId: string) => {
      const res = await fetch(`/api/audits/${auditId}/items/${itemId}/notes/${noteId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete note");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.itemNotes(auditId, itemId) });
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
    },
    onError: () => {
      toast.error("Failed to delete note");
    },
  });
}

const STATUS_LABELS: Record<string, string> = {
  assigned: "Assigned",
  guidance_requested: "Guidance Requested",
  in_progress: "In Progress",
  complete: "Complete",
  blocked: "Blocked",
};

export function useSetAuditStatus(auditId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      status: string;
      blockedReason?: string;
      blockedBy?: "project_team" | "compliance_team";
      resolutionNote?: string;
      recordedInPmApp?: boolean;
      findings?: string;
    }) => {
      const res = await fetch(`/api/audits/${auditId}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to update status");
      return json.data;
    },
    onSuccess: (_, input) => {
      queryClient.invalidateQueries({ queryKey: auditKeys.all });
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
      toast.success(`Status set to ${STATUS_LABELS[input.status] ?? input.status}`);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update status");
    },
  });
}

export function useUpdateAuditTemplates(auditId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { templateIds: string[] }) => {
      const res = await fetch(`/api/audits/${auditId}/templates`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to update templates");
      return json.data as AuditDetail;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.all });
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
      toast.success("Templates updated");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update templates");
    },
  });
}

export function useUpdateAuditAssignee(auditId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { assignedToId: string }) => {
      const res = await fetch(`/api/audits/${auditId}/assignee`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to update assigned auditor");
      return json.data as AuditDetail;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.all });
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
      toast.success("Assigned auditor updated");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update assigned auditor");
    },
  });
}

export function useCreateAuditNote(auditId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) => {
      const res = await fetch(`/api/audits/${auditId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      });
      if (!res.ok) throw new Error("Failed to add note");
      const json = await res.json();
      return json.data as AuditNoteRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
    },
    onError: () => {
      toast.error("Failed to add note");
    },
  });
}

export function useUpdateAuditNote(auditId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ noteId, body }: { noteId: string; body: string }) => {
      const res = await fetch(`/api/audits/${auditId}/notes/${noteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to update note");
      return json.data as AuditNoteRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update note");
    },
  });
}

export function useDeleteAuditNote(auditId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (noteId: string) => {
      const res = await fetch(`/api/audits/${auditId}/notes/${noteId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete note");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
    },
    onError: () => {
      toast.error("Failed to delete note");
    },
  });
}

export function useDeleteAudit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (auditId: string) => {
      const res = await fetch(`/api/audits/${auditId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete audit");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.all });
      toast.success("Audit deleted");
    },
    onError: () => {
      toast.error("Failed to delete audit");
    },
  });
}

export function useComplianceUsers() {
  return useQuery({
    queryKey: complianceUserKeys.all,
    queryFn: async (): Promise<ComplianceUser[]> => {
      const res = await fetch("/api/compliance/users");
      if (!res.ok) throw new Error("Failed to fetch compliance users");
      const json = await res.json();
      return json.data;
    },
  });
}

export interface KickoffRecipient {
  role: string;
  name: string | null;
  email: string;
}

export interface KickoffEmailPreview {
  subject: string;
  body: string;
  to: KickoffRecipient[];
  cc: KickoffRecipient[];
  missing: string[];
  guidanceHoursMissing: boolean;
  ineligibleReason: string | null;
  canSend: boolean;
}

export function useKickoffEmailPreview(auditId: string, enabled: boolean, resend = false) {
  return useQuery({
    queryKey: [...auditKeys.kickoffEmail(auditId), { resend }],
    enabled,
    queryFn: async (): Promise<KickoffEmailPreview> => {
      const res = await fetch(
        `/api/audits/${auditId}/kickoff-email${resend ? "?resend=true" : ""}`,
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to load kickoff email");
      return json.data;
    },
  });
}

export function useSendKickoffEmail(auditId: string, resend = false) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { subject: string; body: string }) => {
      const res = await fetch(`/api/audits/${auditId}/kickoff-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, resend }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to send kickoff email");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
      queryClient.invalidateQueries({ queryKey: auditKeys.kickoffEmail(auditId) });
      toast.success(resend ? "Kickoff email resent" : "Kickoff email sent");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to send kickoff email");
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
      queryClient.invalidateQueries({ queryKey: auditKeys.kickoffEmail(auditId) });
    },
  });
}
